const RELOAD_KEY = 'todesktop:chunk-load-reload-at';
// A reload that still can't load the chunk within this window isn't retried.
const RELOAD_WINDOW_MS = 60_000;

/**
 * Loads a route chunk in the background without letting a failure escape. A
 * failed prefetch means this tab is from an older deploy, which the
 * `vite:preloadError` handler below recovers from.
 */
export const prefetchChunk = (load: () => Promise<unknown>): Promise<void> =>
  load().then(
    () => undefined,
    () => undefined,
  );

/** Records a reload, unless one already happened within the window. */
export const claimChunkReload = (
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now = Date.now(),
): boolean => {
  const lastReloadAt = Number(storage.getItem(RELOAD_KEY));
  if (lastReloadAt && now - lastReloadAt < RELOAD_WINDOW_MS) return false;
  storage.setItem(RELOAD_KEY, String(now));
  return true;
};

/**
 * A tab from an older deploy requests chunks the newer deploy removed. Vite
 * fires `vite:preloadError` when a dynamic import fails; reload once to pick
 * up the current deploy. The error still propagates if no reload happens.
 */
export const installChunkLoadRecovery = (win: Window = window) => {
  win.addEventListener('vite:preloadError', () => {
    try {
      if (!claimChunkReload(win.sessionStorage)) return;
    } catch {
      // Without storage a reload can't be guarded against looping.
      return;
    }
    win.location.reload();
  });
};
